import { useEffect, useState } from "react";
import { FileSpreadsheet, Wallet, Users, Banknote, Percent, RefreshCw, Building2, Trash2, FileDown, Layers3 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import { api, apiErrorMessage, formatRupiah, formatTanggalID } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ExportCsvMandiriDialog } from "@/pages/admin/ExportCsvMandiriDialog";
import { ExportXlsxCustomDialog } from "@/pages/admin/ExportXlsxCustomDialog";
import { ExportPembebananDialog } from "@/pages/admin/ExportPembebananDialog";

const BULAN = [
  "Januari", "Februari", "Maret", "April", "Mei", "Juni",
  "Juli", "Agustus", "September", "Oktober", "November", "Desember",
];
const SEMUA = "__ALL__";

export default function AdminLaporanPayroll() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const now = new Date();
  const [bulan, setBulan] = useState(String(now.getMonth() + 1));
  const [tahun, setTahun] = useState(String(now.getFullYear()));
  const [unitId, setUnitId] = useState(SEMUA);
  const [units, setUnits] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [csvOpen, setCsvOpen] = useState(false);
  const [xlsxOpen, setXlsxOpen] = useState(false);
  const [pembebananOpen, setPembebananOpen] = useState(false);

  const periode = `${tahun}-${String(bulan).padStart(2, "0")}`;

  async function load() {
    setLoading(true);
    try {
      const params = { periode };
      if (unitId !== SEMUA) params.unit_id = unitId;
      const [rep, u] = await Promise.all([
        api.get("/reports/payroll", { params }),
        units.length ? Promise.resolve({ data: units }) : api.get("/master/unit"),
      ]);
      setData(rep.data);
      if (!units.length) setUnits(u.data);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [periode, unitId]);

  async function hapus() {
    const run = data?.run;
    if (!run) return;
    if (!window.confirm(`Hapus hasil Run Payroll ${run.periode_label}? Seluruh slip gaji periode ini akan ikut terhapus.`)) return;
    try {
      await api.delete(`/payroll/run/${run.id}`);
      toast.success("Hasil Run Payroll dihapus");
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  }

  const rows = data?.rows || [];
  const totals = data?.totals || {};
  const run = data?.run;

  return (
    <div className="p-4 sm:p-8 max-w-[1500px] mx-auto" data-testid="page-laporan-payroll">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-teal-700">Administrasi</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 mt-1 leading-tight">
            Laporan Gaji
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Rekapitulasi gaji bulanan seluruh pegawai berdasarkan hasil Run Payroll. Nama periode mengikuti
            bulan tanggal akhir cutoff (bulan gaji). Export Excel dirinci per nama komponen.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} disabled={loading} className="gap-2" data-testid="payroll-report-refresh">
            <RefreshCw size={14} /> Muat Ulang
          </Button>
          <Button onClick={() => setXlsxOpen(true)} disabled={!run} className="bg-teal-700 hover:bg-teal-800 gap-2" data-testid="payroll-export-xlsx">
            <FileSpreadsheet size={14} /> Excel
          </Button>
          <Button
            onClick={() => setPembebananOpen(true)}
            disabled={!run}
            variant="outline"
            className="gap-2 border-emerald-300 text-emerald-800 hover:bg-emerald-50"
            data-testid="payroll-export-pembebanan"
          >
            <Layers3 size={14} /> Rekap Pembebanan
          </Button>
          <Button
            onClick={() => setCsvOpen(true)}
            disabled={!run}
            variant="outline"
            className="gap-2 border-teal-300 text-teal-800 hover:bg-teal-50"
            data-testid="payroll-export-csv-mandiri"
          >
            <FileDown size={14} /> CSV Bank Transfer
          </Button>
          {isAdmin ? (
            <Button variant="outline" onClick={hapus} disabled={!run} className="gap-2 border-rose-200 text-rose-700 hover:bg-rose-50" data-testid="payroll-report-hapus">
              <Trash2 size={14} /> Hapus
            </Button>
          ) : null}
        </div>
      </div>

      <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Bulan Gaji</Label>
          <Select value={bulan} onValueChange={setBulan}>
            <SelectTrigger className="mt-1.5" data-testid="report-bulan"><SelectValue /></SelectTrigger>
            <SelectContent>
              {BULAN.map((b, i) => (<SelectItem key={b} value={String(i + 1)}>{b}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Tahun</Label>
          <Select value={tahun} onValueChange={setTahun}>
            <SelectTrigger className="mt-1.5" data-testid="report-tahun"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[0, 1, 2].map((d) => {
                const y = String(now.getFullYear() - d);
                return <SelectItem key={y} value={y}>{y}</SelectItem>;
              })}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Unit Kerja</Label>
          <Select value={unitId} onValueChange={setUnitId}>
            <SelectTrigger className="mt-1.5" data-testid="report-unit"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={SEMUA}>Semua Unit</SelectItem>
              {units.map((u) => (<SelectItem key={u.id} value={u.id}>{u.nama}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="mt-8 text-center text-slate-500 py-14">Memuat laporan…</div>
      ) : !run ? (
        <div className="mt-6 rounded-xl border border-slate-200 bg-white p-10 text-center" data-testid="payroll-report-empty">
          <div className="mx-auto w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
            <Wallet size={22} />
          </div>
          <div className="mt-3 font-semibold text-slate-700">Belum ada data gaji untuk periode ini</div>
          <div className="text-xs text-slate-500 mt-1">
            Jalankan perhitungan lewat menu <b>Run Payroll</b> terlebih dahulu.
          </div>
        </div>
      ) : (
        <>
          <div className="mt-6 flex items-center gap-2 flex-wrap">
            <span className="font-display text-lg font-extrabold text-slate-900">{run.periode_label}</span>
            <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${
              run.status === "Terbit" ? "bg-emerald-50 text-emerald-700 border-emerald-100" : "bg-amber-50 text-amber-800 border-amber-100"
            }`}>
              {run.status}
            </span>
            <span className="text-[11px] text-slate-500">
              Cutoff {formatTanggalID(run.periode_start)} – {formatTanggalID(run.periode_end)} · Gajian {formatTanggalID(run.tanggal_gajian)}
            </span>
          </div>

          <div className="mt-4 grid grid-cols-2 lg:grid-cols-6 gap-3">
            <Stat icon={Users} label="Pegawai" value={totals.pegawai ?? 0} big />
            <Stat icon={Banknote} label="Total Bruto" value={formatRupiah(totals.bruto)} />
            <Stat icon={Percent} label="PPh 21" value={formatRupiah(totals.pph21)} tone="amber" />
            <Stat icon={Banknote} label="Total Potongan" value={formatRupiah(totals.total_potongan)} tone="rose" />
            <Stat icon={Building2} label="Beban Perusahaan" value={formatRupiah(totals.beban_perusahaan)} tone="indigo" />
            <Stat icon={Wallet} label="Total Dibayarkan" value={formatRupiah(totals.netto)} tone="teal" />
          </div>
          <div className="mt-3 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3 text-xs text-indigo-900 flex flex-wrap items-center gap-x-6 gap-y-1" data-testid="payroll-biaya-perusahaan">
            <span>
              Total Biaya Perusahaan (Bruto + Beban): <b>{formatRupiah(totals.biaya_perusahaan)}</b>
            </span>
            <span>
              PPh 21 dipotong pegawai: <b>{formatRupiah(totals.pph21_dipotong)}</b>
            </span>
            <span>
              PPh 21 ditanggung perusahaan: <b>{formatRupiah(totals.pph21_ditanggung)}</b>
            </span>
            <span>
              Pendapatan tidak tetap: <b>{formatRupiah(totals.bruto_tidak_tetap)}</b>
            </span>
          </div>

          <div className="mt-4 rounded-xl border border-slate-200 bg-white overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 hover:bg-slate-50">
                    {["Pegawai", "Unit", "Jabatan", "Hadir/Kerja", "Telat", "Alpha", "Lembur (jam)", "Gaji Pokok", "Tunjangan Tetap", "Tidak Tetap", "Bruto", "Pot. Absensi", "Pot. Lain", "PPh 21", "Gaji Bersih", "Beban Prsh", "Rekening"].map((h) => (
                      <TableHead key={h} className="uppercase text-[10px] tracking-widest font-bold text-slate-500 whitespace-nowrap">{h}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((s) => (
                    <TableRow key={s.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`payroll-report-row-${s.id}`}>
                      <TableCell>
                        <div className="font-semibold text-slate-900 whitespace-nowrap">{s.nama}</div>
                        <div className="text-[11px] text-slate-500 font-mono-clock">{s.npp} · PTKP {s.ptkp_status}</div>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600 whitespace-nowrap">{s.unit_nama}</TableCell>
                      <TableCell className="text-sm text-slate-600 whitespace-nowrap">{s.jabatan_nama}</TableCell>
                      <TableCell className="text-sm font-mono-clock">{s.hari_hadir}/{s.hari_kerja_periode}</TableCell>
                      <TableCell className="text-sm font-mono-clock">{s.hari_terlambat}</TableCell>
                      <TableCell className="text-sm font-mono-clock">{s.hari_alpha}</TableCell>
                      <TableCell className="text-sm font-mono-clock">{s.total_jam_lembur}</TableCell>
                      <TableCell className="text-sm whitespace-nowrap">{formatRupiah(s.gaji_pokok)}</TableCell>
                      <TableCell className="text-sm whitespace-nowrap text-emerald-700">
                        {formatRupiah((s.bruto_tetap || 0) - (s.gaji_pokok || 0))}
                      </TableCell>
                      <TableCell className="text-sm whitespace-nowrap text-sky-700">
                        {formatRupiah(s.bruto_tidak_tetap)}
                      </TableCell>
                      <TableCell className="text-sm font-semibold whitespace-nowrap">{formatRupiah(s.bruto)}</TableCell>
                      <TableCell className="text-sm text-rose-700 whitespace-nowrap">{formatRupiah(s.potongan_absensi)}</TableCell>
                      <TableCell className="text-sm text-rose-700 whitespace-nowrap">
                        {formatRupiah((s.total_potongan_komponen || 0) - (s.potongan_absensi || 0))}
                      </TableCell>
                      <TableCell className="text-sm text-amber-800 whitespace-nowrap">
                        {formatRupiah(s.pph21_dipotong)}
                        {s.pph21_ditanggung_perusahaan ? (
                          <span className="block text-[10px] text-indigo-700">ditanggung prsh.</span>
                        ) : null}
                      </TableCell>
                      <TableCell className="text-sm font-extrabold text-teal-800 whitespace-nowrap">{formatRupiah(s.netto)}</TableCell>
                      <TableCell className="text-sm text-indigo-700 whitespace-nowrap">{formatRupiah(s.total_beban_perusahaan)}</TableCell>
                      <TableCell className="text-[11px] text-slate-500 whitespace-nowrap">
                        {s.nama_bank || "—"} {s.no_rekening || ""}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </>
      )}

      <ExportCsvMandiriDialog
        open={csvOpen}
        onOpenChange={setCsvOpen}
        run={run}
        unitId={unitId !== SEMUA ? unitId : null}
      />

      <ExportXlsxCustomDialog
        open={xlsxOpen}
        onOpenChange={setXlsxOpen}
        run={run}
        unitId={unitId !== SEMUA ? unitId : null}
      />

      <ExportPembebananDialog
        open={pembebananOpen}
        onOpenChange={setPembebananOpen}
        run={run}
        unitId={unitId !== SEMUA ? unitId : null}
      />
    </div>
  );
}

function Stat({ icon: Icon, label, value, tone, big }) {
  const tones = {
    rose: "text-rose-700 border-rose-200 bg-rose-50",
    amber: "text-amber-800 border-amber-200 bg-amber-50",
    indigo: "text-indigo-700 border-indigo-200 bg-indigo-50",
    teal: "text-teal-800 border-teal-200 bg-teal-50",
  };
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className={`w-8 h-8 rounded-md border flex items-center justify-center ${tones[tone] || "bg-slate-50 text-slate-600 border-slate-200"}`}>
        <Icon size={14} />
      </div>
      <div className={`mt-2 font-extrabold text-slate-900 leading-tight ${big ? "text-2xl" : "text-base"}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold mt-0.5">{label}</div>
    </div>
  );
}
